/**
 * The teaching workspace root (issues #16, #465 part 1a).
 *
 * One authenticated attention read backs this page:
 * `GET /api/lms/workspaces/teaching/today`. `/admin/teaching` and `/instructor`
 * stay one component, and `basePath` is what says which shell is rendering.
 * The page composes ADR 0035's Teaching Home row over that read: the ranked
 * next step, the edition grid, the "This week" register (sprint decision Q1,
 * read through the same cached course and session reads My sessions holds),
 * the attention register, and every other course behind its disclosure.
 *
 * Every href in the projection is ALREADY correct for the shell that received
 * it — the server emits it from `shell_base`, decided by the same rule the
 * course home uses — so the client-side shell rewrite this page used to carry
 * is gone rather than reproduced. Two places deciding one link shape is how
 * they come to disagree.
 *
 * `/dashboard/instructor` is still served and is unchanged. Reverting this file
 * restores the shipped dashboard; that is the whole rollback.
 */
import { Link } from "react-router";

import { DashboardNotice } from "@/components/dashboard/DashboardNotice";
import { FrameHeader } from "@/components/workspace/FrameHeader";
import { TeachingHomeView } from "@/components/workspace/TeachingHomeView";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useSeatWorkspace } from "@/hooks/use-seat-workspace";
import { useTeachingWeek } from "@/hooks/use-teaching-week";
import { ApiError } from "@/lib/api/client";
import type { TeachingSeatProjection } from "@/lib/api/seatWorkspace";
import { greetingSuffix } from "@/lib/greeting";
import { loadState } from "@/lib/query-state";
import { PROTECTED_ROUTES } from "@/navigation/routes";

export function InstructorDashboardPage({ basePath = "/instructor" }: { basePath?: string }) {
  const { me } = useAuth();
  const query = useSeatWorkspace("teaching");
  const { refetch } = query;
  const authorityRefused = query.error instanceof ApiError && (query.error.status === 401 || query.error.status === 403);
  const data = !authorityRefused && query.data?.perspective === "teaching" ? query.data as TeachingSeatProjection : undefined;
  const state = authorityRefused ? "error" : loadState(query);
  const shell = basePath === PROTECTED_ROUTES.adminTeaching.dashboard ? "adminTeaching" : "instructor";
  const week = useTeachingWeek({
    shell,
    courseIds: new Set(data?.courses.state === "ready" ? data.courses.value.items.map((course) => course.course_id) : []),
    reference: data?.generated_at ?? "",
    enabled: data?.courses.state === "ready",
  });
  // The one quiet act to My sessions: the week register's head act, or the
  // frame's when the week has nothing to show.
  const allClasses = (
    <Button asChild variant="subtle" size="sm">
      <Link to={PROTECTED_ROUTES[shell].sessions}>My sessions</Link>
    </Button>
  );
  // The stable week List now owns My sessions even when its read is empty.
  const weekInFrame = false;
  // One non-disclosing 404 covers no assignment, an ended assignment, the wrong
  // tenant, and a stranger alike. It is settled, so it is not retried.
  // ONLY the 404. A 401 is a credential mid-refresh and a 403 is a membership
  // that is inactive or unresolved; both recover, so they keep the retryable
  // error surface below rather than claiming there is no record.
  const noSeat = query.isNoSeat || (
    query.error instanceof ApiError && query.error.status === 404
  );
  const greeting = greetingSuffix(me?.honorific, me?.preferred_name, me?.full_name);
  const term = data?.active_terms.length === 1
    ? data.active_terms[0].name
    : data && data.active_terms.length > 1
      ? `${data.active_terms.length} active terms`
      : data?.term.state === "ready" ? data.term.value.name : null;

  return (
    <div className="app-page min-w-0">
      {/* The term as the eyebrow and the greeting as the page object. All
          sessions is reached from the week register's head. */}
      <FrameHeader
        eyebrow={term}
        title={<>As-salāmu ʿalaykum{greeting}</>}
        quietAction={weekInFrame ? allClasses : undefined}
      />

      {!data && noSeat && <DashboardNotice state="absent" perspective="teaching" onRetry={() => void refetch()} />}
      {!data && !noSeat && state === "error" && <DashboardNotice state="error" perspective="teaching" onRetry={() => void refetch()} />}
      {!noSeat && (data || state === "loading" || state === "paused") && (
        <TeachingHomeView key={`${me?.tenant_id}:${me?.user_id}`} projection={data} week={week} stale={query.isError} onRetry={() => void refetch()} />
      )}
    </div>
  );
}
